import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    AvatarUploadBadgeConstantType,
    AvatarUploadDynamicStyleType,
    AvatarUploadBadgeMeasures
} from "./type";
import type {
    Mutable
} from "../../../../types";
import type {
    AvatarSize
} from "../../../avatar/type";
import type {
    FileUploadSize
} from "../../type";
import {
    getElevationStyle
} from "../../../../helpers/theme/elevation";
import {
    webStyle
} from "../../../../utils";

export const AVATAR_UPLOAD_SIZES: Record<FileUploadSize, AvatarSize> = {
    large: "xLarge",
    medium: "large",
    small: "medium"
};

export const AVATAR_UPLOAD_GAPS: Record<FileUploadSize, keyof NCoreUIKit.Spaces> = {
    medium: "spacingLg",
    large: "spacingLg",
    small: "spacingMd"
};

export const getAvatarUploadBadge = ({
    controlHeights,
    touchTargets,
    iconSizes,
    borders,
    size
}: AvatarUploadBadgeConstantType): AvatarUploadBadgeMeasures => {
    if(size === "small") {
        return {
            iconSize: iconSizes.small - borders.indicator,
            size: iconSizes.large
        };
    }

    return {
        size: size === "medium" ? touchTargets.fine : controlHeights.small,
        iconSize: iconSizes.small
    };
};

const stylesheet = StyleSheet.create({
    progress: {
        ...StyleSheet.absoluteFill,
        justifyContent: "center",
        pointerEvents: "none",
        alignItems: "center"
    },
    media: {
        flexShrink: 0,
        ...webStyle({
            outlineStyle: "solid",
            cursor: "pointer",
            outlineWidth: 0
        })
    },
    mediaDisabled: {
        ...webStyle({
            cursor: "not-allowed"
        })
    },
    badge: {
        justifyContent: "center",
        alignItems: "center",
        position: "absolute"
    },
    disc: {
        justifyContent: "center",
        alignItems: "center"
    },
    container: {
        alignItems: "center",
        flexDirection: "row",
        minWidth: 0
    },
    actions: {
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    rule: {
        fontVariant: [
            "tabular-nums"
        ]
    },
    body: {
        flexShrink: 1,
        minWidth: 0
    },
    text: {
        minWidth: 0
    }
});

export const useStyles = ({
    controlHeights,
    touchTargets,
    avatarSizes,
    shadowAlpha,
    elevations,
    isDisabled,
    iconSizes,
    borders,
    colors,
    spaces,
    size
}: AvatarUploadDynamicStyleType) => {
    const diameter = avatarSizes[AVATAR_UPLOAD_SIZES[size]];

    const badge = getAvatarUploadBadge({
        controlHeights,
        touchTargets,
        iconSizes,
        borders,
        size
    });

    const discSize = controlHeights[size] + spaces.spacingXs * 2;

    const styles = {
        badge: {
            backgroundColor: isDisabled ? colors.system.state.overlay.disabled.neutral : colors.content.container.raised,
            borderColor: colors.content.container.raised,
            borderWidth: borders.subtract,
            bottom: -borders.subtract,
            borderRadius: badge.size,
            end: -borders.subtract,
            height: badge.size,
            width: badge.size
        } as Mutable<ViewStyle>,
        disc: {
            backgroundColor: colors.content.container.raised,
            borderRadius: discSize,
            height: discSize,
            width: discSize
        } as Mutable<ViewStyle>,
        container: {
            columnGap: spaces[AVATAR_UPLOAD_GAPS[size]]
        } as Mutable<ViewStyle>,
        actions: {
            columnGap: spaces.spacingSm,
            rowGap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        text: {
            rowGap: spaces.spacingXxs
        } as Mutable<ViewStyle>,
        body: {
            rowGap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        media: {
            borderRadius: diameter,
            height: diameter,
            width: diameter
        } as Mutable<ViewStyle>,
        progress: {
            borderRadius: diameter
        } as Mutable<ViewStyle>
    };

    if(!isDisabled) {
        Object.assign(styles.badge, getElevationStyle({
            shadowColor: colors.system.shadow,
            level: "level1",
            shadowAlpha,
            elevations
        }));
    }

    Object.assign(styles.disc, getElevationStyle({
        shadowColor: colors.system.shadow,
        level: "level1",
        shadowAlpha,
        elevations
    }));

    return styles;
};
export default stylesheet;
